import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AuditFeeCategory } from '../../../models/auditFeeCategory';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { ColumnConfig, LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-audit-fee-category-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './audit-fee-category-list.component.html',
  styleUrl: './audit-fee-category-list.component.scss'
})
export class AuditFeeCategoryListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  auditFeeCategories = signal<AuditFeeCategory[]>([]);

  auditFeeCategoriesColumns: ColumnConfig[] = [
    { key: 'name', header: 'Name', type: 'text', sortable: true, editable: true },
    {
      key: 'primaryCategory',
      header: 'Category',
      type: 'select',
      sortable: true,
      editable: true,
      selectOptions: [
        { value: 1, label: 'Audit' },
        { value: 2, label: 'Related' },
        { value: 3, label: 'Tax' },
        { value: 4, label: 'Other' }
      ]
    },
    { key: 'rank', header: 'Rank', type: 'number', sortable: true, editable: true }
  ];

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadAuditFeeCategories();
  }

  loadAuditFeeCategories(): void {
    this.spinnerService.show();
    this.lutService.getAuditFeeCategories().subscribe({
      next: (data) => {
        const sortedData = this.sortAuditFeeCategories(data);
        this.auditFeeCategories.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading audit fee categories:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortAuditFeeCategories(categories: AuditFeeCategory[]): AuditFeeCategory[] {
    return [...categories].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateAuditFeeCategory(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(auditFeeCategory: AuditFeeCategory): void {
  }

  onEditAuditFeeCategory(auditFeeCategory: AuditFeeCategory): void {
  }

  onDeleteAuditFeeCategory(auditFeeCategory: AuditFeeCategory): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(auditFeeCategory.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalCategories = this.auditFeeCategories();

          // 1. Optimistically remove from local state immediately
          this.auditFeeCategories.update(categories => categories.filter(c => c.id !== auditFeeCategory.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteAuditFeeCategory(auditFeeCategory.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Audit fee category deleted successfully');
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting audit fee category:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.auditFeeCategories.set(originalCategories);
              this.toast.error(`<strong>Failed to delete Audit Fee Category:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: AuditFeeCategory, newName: string} | {item: AuditFeeCategory, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: AuditFeeCategory;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.auditFeeCategories.update(categories => {
      const updated = categories.map(c => c.id === event.item.id ? updatedItem : c);
      return this.sortAuditFeeCategories(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateAuditFeeCategory(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Audit fee category updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.auditFeeCategories.update(categories => {
          const updated = categories.map(c => c.id === event.item.id ? response : c);
          return this.sortAuditFeeCategories(updated);
        });
      },
      error: (error) => {
        console.error('Error updating audit fee category:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.auditFeeCategories.update(categories =>
          categories.map(c => c.id === event.item.id ? originalItem : c)
        );
        this.toast.error(`<strong>Failed to update Audit Fee Category:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newCategory: AuditFeeCategory = {
      id: tempId,
      name: event['name'],
      primaryCategory: event['primaryCategory'] ?? 4,
      rank: event['rank'] ?? 0,
      systemReserved: false
    };

    // 1. Optimistically add to local state immediately and sort
    this.auditFeeCategories.update(categories => this.sortAuditFeeCategories([...categories, newCategory]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createAuditFeeCategory({
      name: event['name'],
      primaryCategory: event['primaryCategory'] ?? 4,
      rank: event['rank'] ?? 0,
      systemReserved: false
    }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Audit fee category created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.auditFeeCategories.update(categories => {
          const updated = categories.map(c => c.id === tempId ? response : c);
          return this.sortAuditFeeCategories(updated);
        });
      },
      error: (error) => {
        console.error('Error creating audit fee category:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.auditFeeCategories.update(categories =>
          categories.filter(c => c.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Audit Fee Category:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }
}
